import { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Drawer, Form, Input, Spin } from 'antd';
import { SaveOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { getApiErrorMessage } from '@api/server';
import { createSpace, getSpace, updateSpace } from './spaces';

const Space = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { id } = useParams();
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const spaceId = useMemo(() => {
    if (!id) {
      return null;
    }

    const nextId = Number(id);
    return Number.isInteger(nextId) && nextId > 0 ? nextId : null;
  }, [id]);
  const isNewSpace = !id;
  const title = isNewSpace ? 'Новое пространство' : `Пространство #${spaceId || ''}`;
  const closeDrawer = useCallback(() => {
    navigate('/spaces');
  }, [navigate]);

  const loadSpace = useCallback(async () => {
    if (isNewSpace) {
      form.setFieldsValue({ name: '' });
      return;
    }

    if (!spaceId) {
      setError('Некорректный идентификатор пространства.');
      return;
    }

    setError('');
    setIsLoading(true);
    try {
      const space = await getSpace(spaceId);
      form.setFieldsValue({ name: space.name });
    } catch (requestError) {
      setError(getApiErrorMessage(requestError));
    } finally {
      setIsLoading(false);
    }
  }, [form, isNewSpace, spaceId]);

  useEffect(() => {
    loadSpace();
  }, [loadSpace]);

  const handleSave = async (values) => {
    setError('');
    setIsSaving(true);
    try {
      const payload = {
        name: values.name?.trim(),
      };

      if (isNewSpace) {
        await createSpace(payload);
      } else {
        await updateSpace({
          id: spaceId,
          ...payload,
        });
      }

      navigate('/spaces', { state: { spacesRefreshKey: Date.now() } });
    } catch (requestError) {
      setError(getApiErrorMessage(requestError));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Drawer
      className="user-drawer user-form-drawer"
      title={title}
      placement="right"
      width={560}
      open
      onClose={closeDrawer}
      destroyOnHidden
      footer={(
        <div className="user-drawer-footer">
          <Button type="primary" icon={<SaveOutlined />} loading={isSaving} onClick={() => form.submit()}>
            Сохранить
          </Button>
          <Button onClick={closeDrawer}>Отмена</Button>
        </div>
      )}
    >
      {error && (
        <Alert className="user-alert" type="error" message={error} showIcon />
      )}

      <Spin spinning={isLoading}>
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSave}
          requiredMark={false}
          initialValues={{ name: '' }}
        >
          <Form.Item
            label="Название"
            name="name"
            rules={[
              { required: true, message: 'Введите название пространства.' },
              { max: 255, message: 'Название должно быть не длиннее 255 символов.' },
            ]}
          >
            <Input placeholder="Название пространства" autoFocus />
          </Form.Item>
        </Form>
      </Spin>
    </Drawer>
  );
};

export default Space;
